import React from "react";
import PropTypes from "prop-types";
import { useRouter } from "next/router";
import { HeartIcon as HeartIconOutline } from "@heroicons/react/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/solid";
import { classNames, Pressable } from "@gonoodle/gn-universe-ui";
import { useMutation, useQuery } from "react-query";
import {
  CONTENT_TYPES,
  SECTIONS,
} from "@gonoodle/gn-universe-analytics-schema/src/constants";
import { useUser } from "../contexts/user";
import api from "../api";
import { QUERY_KEYS } from "../constants";
import { useLogEvent } from "../contexts/Analytics";
import { getUtmParamsFromQueryParams } from "../utils/analytics";
import { useProfile } from "../hooks";

const client = api();

export default function FavoriteButton({
  id,
  title,
  analyticsProps,
  type,
  variant,
}) {
  const { user } = useUser();
  const router = useRouter();
  const { profile } = useProfile();

  const canFavorite = user.isLoggedIn;

  const { data: favorite, refetch } = useQuery(
    [QUERY_KEYS.FAVORITES],
    () => client.getFavorites(),
    {
      enabled: canFavorite,
      select: (favorites) =>
        favorites.find((fav) => fav.type === type && fav.activity.id === id),
    },
  );

  const favorited = Boolean(favorite);

  const { logEvent: logAddToFavoritesEvent } = useLogEvent({
    event: "Add to Favorites",
    properties: {
      contentType: type,
      id,
      title,
      ...analyticsProps,
      sourceElement: SECTIONS.FAVORITE_BUTTON,
      sourceName: SECTIONS.FAVORITE_BUTTON,
      gradeId: profile?.grade.id,
      ...getUtmParamsFromQueryParams(router.query),
    },
  });

  const { logEvent: logRemoveFromFavoritesEvent } = useLogEvent({
    event: "Remove from Favorites",
    properties: {
      contentType: type,
      id,
      title,
      ...analyticsProps,
      sourceElement: SECTIONS.FAVORITE_BUTTON,
      sourceName: SECTIONS.FAVORITE_BUTTON,
      gradeId: profile?.grade.id,
    },
  });

  const { mutate: toggle, isLoading } = useMutation(
    () => {
      if (favorited) {
        return client.unfavorite({ type, id });
      }
      return client.favorite({ type, id });
    },
    {
      onSuccess: () => {
        refetch();
        if (favorited) {
          logRemoveFromFavoritesEvent();
        } else {
          logAddToFavoritesEvent();
        }
      },
    },
  );

  if (!canFavorite) {
    return null;
  }

  return (
    <Pressable
      elementType="button"
      className={classNames(
        "cursor-pointer z-10 relative",
        isLoading && "opacity-50",
      )}
      onPress={() => {
        if (!isLoading) {
          toggle();
        }
      }}
    >
      {variant === "icon" ? (
        <React.Fragment>
          <span className="sr-only">
            {favorited ? "Unfavorite" : "Favorite"}
          </span>
          {favorited ? (
            <HeartIconSolid className="h-md text-white" />
          ) : (
            <HeartIconOutline className="h-md text-gray-500" />
          )}
        </React.Fragment>
      ) : (
        <div
          className={classNames(
            "flex h-button-md px-sm rounded items-center gap-xs justify-center",
            favorited ? "bg-white" : "border border-white",
          )}
        >
          <span
            className={classNames(
              "text-sm font-bold",
              favorited ? "text-gray-900" : "text-white",
            )}
          >
            {favorited ? "Favorited" : "Favorite"}
          </span>
          {favorited ? (
            <HeartIconSolid className="h-md text-gray-900" />
          ) : (
            <HeartIconOutline className="h-md text-white" />
          )}
        </div>
      )}
    </Pressable>
  );
}

FavoriteButton.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  analyticsProps: PropTypes.shape({
    sourcePage: PropTypes.string,
    sourcePageType: PropTypes.string,
  }),
  type: PropTypes.oneOf([CONTENT_TYPES.VIDEO, CONTENT_TYPES.ACTIVITY])
    .isRequired,
  variant: PropTypes.oneOf(["button", "icon"]),
};

FavoriteButton.defaultProps = {
  variant: "icon",
};
